<template>
  <div class="fill-height">
    <line-chart
      v-if="viewMode === 'chart'"
      class="fill-height pb-0"
      type="bar"
      :height="220"
      :raw-data="accelerationZones"
      :chart-value-keys="[
        { key: $t('table_headers.decelerations'), type: 'bar'},
        { key: $t('table_headers.accelerations'), type: 'bar'}
      ]"
      :plot-options="{
        bar: {
          borderRadius: 5,
          borderRadiusApplication: 'end',
          borderRadiusWhenStacked: 'all',
          horizontal: true,
          barHeight: '60%',
          columnWidth: '90%',
        }
      }"
      x-value="key"
      :grid="{ show: false }"
      :xaxis="{
        categories: accelerationZones.map(stat => stat.label),
        tickPlacement: 'between',
        min: -maxValue,
        max: maxValue,
        axisBorder: { show: false },
        labels: {
          formatter: function(value) {
            return Math.round(Math.abs(value));
          }
        }
      }"
      :tooltip="{
        x: {
          formatter: function(value) {
            return value;
          }
        },
        y: {
          formatter: function(value) {
            return Math.abs(value).toFixed(1) + ' m';
          }
        }
      }"
      :stacked="true"
    >
      <template v-slot:header>
        <v-card-title class="pa-6 d-flex justify-space-between">
          <div class="subtitle-2 grey--text text--grey-100">
            {{ $t("card_title.acceleration_distance_zone_breakdown") }}{{ aggregation ? ": " + $t(`variables.${aggregation}.long`) : '' }}
          </div>
          <v-btn
            v-if="removable"
            icon
            style="pointer-events: auto"
            @click="$emit('remove')"
          >
            <v-icon small>
              fal fa-times
            </v-icon>
          </v-btn>
          <visual-list-toggle
            v-else
            :value.sync="viewMode"
          />
        </v-card-title>
      </template>
    </line-chart>

    <v-card
      v-else-if="viewMode === 'table'"
      class="fill-height rounded-lg"
    >
      <v-card-title class="pa-6 d-flex justify-space-between">
        <div class="subtitle-2 grey--text text--grey-100">
          {{ $t("card_title.acceleration_distance_zone_breakdown") }}{{ aggregation ? ": " + $t(`variables.${aggregation}.long`) : '' }}
        </div>
        <div class="d-flex align-center">
          <visual-list-toggle
            v-if="!removable"
            :value.sync="viewMode"
          />
        </div>
      </v-card-title>

      <v-data-table
        :headers="tableHeaders"
        :items="accelerationZones"
        hide-default-footer
        disable-pagination
        :items-per-page="-1"
        fixed-header
        dense
        :height="220"
      >
        <template v-slot:item.Decelerations="{ item }">
          {{ Math.abs(item.Decelerations).toFixed(1) }}
        </template>
        <template v-slot:item.Accelerations="{ item }">
          {{ item.Accelerations.toFixed(1) }}
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import LineChart from '@/components/charts/LineChart.vue'
import VisualListToggle from '@/components/buttons/VisualListToggle.vue'
import { hasSixZoneSetup } from '@/utils/helpers'

export default {
  name: 'AccelerationDistanceZoneChart',
  components: { LineChart, VisualListToggle },
  props: {
    stats: {
      type: Object,
      required: true
    },
    removable: {
      type: Boolean,
      default: false
    },
    aggregation: {
      type: String,
      default: null
    }
  },
  data: () => ({
    viewMode: 'chart'
  }),
  computed: {
    accelerationZones() {
      if (hasSixZoneSetup()) {
        return [
          {
            key: 'ACCZONE6TD',
            label: this.$t('variables.zone6'),
            Accelerations: this.getStatValue('ACCZONE6TD') || 0,
            Decelerations: -(this.getStatValue('DECZONE6TD') || 0)
          },
          {
            key: 'ACCZONE5TD',
            label: this.$t('variables.zone5'),
            Accelerations: this.getStatValue('ACCZONE5TD') || 0,
            Decelerations: -(this.getStatValue('DECZONE5TD') || 0)
          },
          {
            key: 'ACCZONE4TD',
            label: this.$t('variables.zone4'),
            Accelerations: this.getStatValue('ACCZONE4TD') || 0,
            Decelerations: -(this.getStatValue('DECZONE4TD') || 0)
          },
          {
            key: 'ACCZONE3TD',
            label: this.$t('variables.zone3'),
            Accelerations: this.getStatValue('ACCZONE3TD') || 0,
            Decelerations: -(this.getStatValue('DECZONE3TD') || 0)
          },
          {
            key: 'ACCZONE2TD',
            label: this.$t('variables.zone2'),
            Accelerations: this.getStatValue('ACCZONE2TD') || 0,
            Decelerations: -(this.getStatValue('DECZONE2TD') || 0)
          },
          {
            key: 'ACCZONE1TD',
            label: this.$t('variables.zone1'),
            Accelerations: this.getStatValue('ACCZONE1TD') || 0,
            Decelerations: -(this.getStatValue('DECZONE1TD') || 0)
          }
        ]
      }
      return [
        {
          key: 'ACCZONE4TD',
          label: this.$t('variables.zone4'),
          Accelerations: this.getStatValue('ACCZONE4TD') || 0,
          Decelerations: -(this.getStatValue('DECZONE4TD') || 0)
        },
        {
          key: 'ACCZONE3TD',
          label: this.$t('variables.zone3'),
          Accelerations: this.getStatValue('ACCZONE3TD') || 0,
          Decelerations: -(this.getStatValue('DECZONE3TD') || 0)
        },
        {
          key: 'ACCZONE2TD',
          label: this.$t('variables.zone2'),
          Accelerations: this.getStatValue('ACCZONE2TD') || 0,
          Decelerations: -(this.getStatValue('DECZONE2TD') || 0)
        }
      ]
    },
    maxValue() {
      return Math.max(
        ...this.accelerationZones.map(stat => stat.Accelerations),
        ...this.accelerationZones.map(stat => Math.abs(stat.Decelerations))
      ) + 1
    },
    tableHeaders() {
      return [
        {
          text: this.$t('table_headers.zone'),
          value: 'label',
          sortable: true,
          width: '40%'
        },
        {
          text: this.$t('table_headers.accelerations') + ' [m]',
          value: 'Accelerations',
          sortable: true,
          align: 'center'
        },
        {
          text: this.$t('table_headers.decelerations') + ' [m]',
          value: 'Decelerations',
          sortable: true,
          align: 'center'
        }
      ]
    }
  },
  methods: {
    getKeyWithAggregation(key) {
      return this.aggregation && !this.removable ? `${key}_${this.aggregation}` : key
    },
    getStatValue(key) {
      const aggregatedKey = this.getKeyWithAggregation(key)
      return this.stats[aggregatedKey]
    }
  }
}
</script>
